import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   COMPANY DETAILS (change here if anything changes)
------------------------------------------------------------------- */
const COMPANY = "Guardian Facility Services Ltd";
const COMPANY_NO = "17521897";
const ADDRESS = "78 Compton Avenue, E6 3DP";
const PHONE = "07459 262621";
const PHONE_LINK = "tel:07459262621";
const EMAIL = "guardianfacilityservicesltd@gmail.com";
const EMAIL_LINK = `https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL}`;
const LAST_UPDATED = "17 October 2026";

/* ------------------------------------------------------------------
   TERMS CONTENT
------------------------------------------------------------------- */
const sections = [
  {
    id: "about",
    title: "About these terms",
    body: (
      <>
        <p>
          These terms apply to all services provided by {COMPANY} (Company No. {COMPANY_NO}), whose
          office is at {ADDRESS} (“we”, “us”, “our”), and to your use of this website. By booking
          our services, accepting a quote or using this website, you agree to these terms.
        </p>
        <p>
          “You” means the person or business that books or receives our services. If you book on
          behalf of a company or another person, you confirm that you have authority to do so.
        </p>
      </>
    ),
  },
  {
    id: "services",
    title: "Our services",
    body: (
      <>
        <p>We provide the following services for residential and commercial properties:</p>
        <ul>
          <li><strong>Property rental and lettings,</strong> including tenant sourcing, referencing, rent collection and tenancy administration.</li>
          <li><strong>Property maintenance and handyman work,</strong> including repairs, planned upkeep and arranging safety certificates.</li>
          <li><strong>Professional cleaning,</strong> including domestic, end-of-tenancy and commercial cleaning.</li>
        </ul>
        <p>
          The exact scope of each job is set out in your quote, booking confirmation or written
          agreement. Anything not included there is outside the agreed service.
        </p>
      </>
    ),
  },
  {
    id: "quotes",
    title: "Quotes and bookings",
    body: (
      <>
        <p>
          Estimates given through the website, by phone or by email are based on the information you
          give us. A quote is valid for the period stated on it. A booking is confirmed only when we
          confirm it to you in writing (email or message) or begin the work by agreement.
        </p>
        <p>
          If the job turns out to be different from the information provided, we will tell you and
          agree any change to the price or scope with you before continuing.
        </p>
      </>
    ),
  },
  {
    id: "access",
    title: "Access to the property",
    body: (
      <p>
        You must make sure we can safely access the property at the agreed time, including keys,
        entry codes, parking where needed, and water and electricity supplies. If we cannot gain
        access, or have to wait or return because access was not available, we may charge for the
        wasted visit or for additional time.
      </p>
    ),
  },
  {
    id: "payment",
    title: "Prices and payment",
    body: (
      <>
        <p>
          Prices are as stated in your quote or invoice, in pounds sterling. Unless we agree
          otherwise in writing, payment is due by the date shown on the invoice. We will tell you
          about any VAT that applies.
        </p>
        <p>
          If an invoice is not paid on time, we may pause further work until payment is received.
          For business customers, we may charge interest and compensation as permitted by the Late
          Payment of Commercial Debts (Interest) Act 1998.
        </p>
      </>
    ),
  },
  {
    id: "cancellation",
    title: "Cancellation and rescheduling",
    body: (
      <>
        <p>
          Please give us as much notice as you can if you need to cancel or move a booking. The
          notice period and any cancellation charge that apply are stated in your quote or booking
          confirmation. Last-minute cancellations may be charged where we have already committed
          staff, time or materials.
        </p>
        <p>
          <strong>Consumers:</strong> if you are an individual and book with us online, by phone or by
          email, you usually have the right to cancel within 14 days of agreeing to the booking. If
          you ask us to start the work within that 14-day period, you will need to pay for the
          services already provided up to the point you cancel. Nothing in these terms affects your
          statutory rights.
        </p>
      </>
    ),
  },
  {
    id: "lettings",
    title: "Lettings services",
    body: (
      <>
        <p><strong>Landlords</strong> must give us accurate information about the property and are responsible for making sure it is legally fit to let. This includes holding the required safety certificates and complying with the relevant legislation. We can arrange these for you as part of our maintenance services.</p>
        <p><strong>Tenants and applicants</strong> must give us accurate and complete information. Referencing and credit checks are carried out with your consent, and we may decline an application where checks are not satisfactory or information is false or incomplete.</p>
        <p>Tenancy deposits are protected in an approved tenancy deposit protection scheme as required by law. Any tenancy agreement you sign will contain the specific terms of that tenancy.</p>
      </>
    ),
  },
  {
    id: "maintenance",
    title: "Maintenance and emergency work",
    body: (
      <>
        <p>
          We carry out maintenance work with reasonable skill and care. If we find extra work is
          needed that was not in the original quote, we will explain it and obtain your approval
          before carrying it out, except where it is urgent and necessary to make a situation safe.
        </p>
        <p>
          For emergency callouts, we will do our best to respond quickly, but we cannot guarantee a
          specific arrival time. Some work may need specialist parts or a separate regulated
          contractor, and we will tell you if so.
        </p>
      </>
    ),
  },
  {
    id: "cleaning",
    title: "Cleaning services",
    body: (
      <>
        <p>
          Our cleaning teams use professional methods and products suitable for the surfaces being
          cleaned. Please tell us in advance about any delicate surfaces, allergies, pets or items
          that need special care.
        </p>
        <p>
          We cannot guarantee the removal of permanent stains, damage or wear that existed before we
          started. If you are not satisfied with a clean, please contact us promptly after it is
          completed so that we can inspect and, where appropriate, put it right.
        </p>
      </>
    ),
  },
  {
    id: "responsibilities",
    title: "Your responsibilities",
    body: (
      <ul>
        <li>Provide accurate information about yourself, your property and the work you need.</li>
        <li>Tell us about any hazards at the property, such as unsafe structures, asbestos or aggressive animals.</li>
        <li>Remove or secure cash, valuables and personal items before our teams arrive.</li>
        <li>Treat our staff and subcontractors with courtesy and respect. We may stop work if they feel unsafe.</li>
      </ul>
    ),
  },
  {
    id: "liability",
    title: "Our liability",
    body: (
      <>
        <p>
          Nothing in these terms limits or excludes our liability for death or personal injury caused
          by our negligence, for fraud or fraudulent misrepresentation, or for anything else that
          cannot lawfully be limited or excluded.
        </p>
        <p>
          Subject to that, we are responsible for loss or damage you suffer that is a foreseeable
          result of our failure to use reasonable skill and care. We are not liable for loss of
          profit, loss of business, or indirect losses that were not foreseeable when you booked
          with us. For business customers, our total liability for any one job is limited to the
          price paid for that job.
        </p>
        <p>
          If you believe we have damaged your property, please tell us as soon as possible and
          before the damage is repaired, so that we can inspect it and deal with the claim.
        </p>
      </>
    ),
  },
  {
    id: "complaints",
    title: "Complaints",
    body: (
      <p>
        We want to get things right. If you are unhappy with any part of our service, please contact
        us using the details below, explaining what went wrong. We will acknowledge your complaint
        and work with you to resolve it fairly and promptly.
      </p>
    ),
  },
  {
    id: "beyond-control",
    title: "Events outside our control",
    body: (
      <p>
        We are not responsible for delays or failure to carry out our services caused by events
        outside our reasonable control, such as severe weather, utility or transport failures,
        illness, industrial action or government restrictions. If this happens, we will let you know
        and rearrange the work as soon as we reasonably can.
      </p>
    ),
  },
  {
    id: "website",
    title: "Website use and privacy",
    body: (
      <p>
        The content of this website is provided for general information and may be updated at any
        time. The text, logos and images on this website belong to us or our licensors and may not be
        copied or reused without permission. We handle personal information as described in our{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>
    ),
  },
  {
    id: "general",
    title: "General",
    body: (
      <ul>
        <li>These terms, together with your quote or booking confirmation, are the whole agreement between you and us for the services.</li>
        <li>We may update these terms from time to time. The version in force when you book applies to that booking.</li>
        <li>If any part of these terms is found to be unenforceable, the rest remains in force.</li>
        <li>You may not transfer your booking to someone else without our written agreement.</li>
        <li>Delay in enforcing any of our rights does not mean we have given them up.</li>
      </ul>
    ),
  },
  {
    id: "law",
    title: "Governing law",
    body: (
      <p>
        These terms are governed by the law of England and Wales, and the courts of England and Wales
        have jurisdiction over any dispute, except that if you are a consumer living in Scotland or
        Northern Ireland you may also bring proceedings in the courts of the part of the UK where you
        live.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <>
        <p>If you have any questions about these terms, please contact us:</p>
        <ul className="tc-contact">
          <li><strong>Company:</strong> {COMPANY} (Company No. {COMPANY_NO}), registered in England and Wales</li>
          <li><strong>Address:</strong> {ADDRESS}</li>
          <li><strong>Phone:</strong> <a href={PHONE_LINK}>{PHONE}</a></li>
          <li><strong>Email:</strong> <a href={EMAIL_LINK} target="_blank" rel="noopener noreferrer">{EMAIL}</a></li>
        </ul>
      </>
    ),
  },
];

/* ------------------------------------------------------------------
   PAGE CSS (everything for the Terms page is in this file)
------------------------------------------------------------------- */
const css = `
.tc-banner{background:var(--navy-deep);color:#fff;padding-top:80px;padding-bottom:80px;border-bottom:4px solid var(--gold)}
.tc-banner h1{color:#fff;font-size:clamp(32px,4.6vw,48px);font-weight:800;letter-spacing:-.5px;margin-bottom:12px;animation:tcRise .7s ease-out both}
@keyframes tcRise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
.tc-banner p{margin:0;color:#C4CFDA;font-size:18px}

.tc-wrap{padding-top:72px;padding-bottom:96px;display:grid;grid-template-columns:280px 1fr;gap:64px;align-items:start}
.tc-toc{position:sticky;top:110px;max-height:calc(100vh - 140px);overflow-y:auto;border:1px solid #E1E6EB;border-radius:6px;padding:24px 22px;background:var(--paper)}
.tc-toc h2{font-size:17px;margin-bottom:14px}
.tc-toc ol{list-style:none;margin:0;padding:0;counter-reset:toc}
.tc-toc li{counter-increment:toc;margin-bottom:2px}
.tc-toc a{display:flex;gap:10px;padding:6px 0;color:var(--muted);text-decoration:none;font-size:15px;line-height:1.35}
.tc-toc a::before{content:counter(toc) ".";font-family:var(--font-head);font-weight:600;color:var(--silver);min-width:24px}
.tc-toc a:hover{color:var(--navy)}

.tc-content{counter-reset:sec}
.tc-intro{font-size:19px;color:var(--muted);padding-bottom:28px;margin-bottom:12px;border-bottom:1px solid var(--mist)}
.tc-sec{scroll-margin-top:110px;padding-top:30px;counter-increment:sec}
.tc-sec h2{font-size:26px;font-weight:800;display:flex;gap:14px;align-items:baseline}
.tc-sec h2::before{content:counter(sec);font-family:var(--font-head);font-size:14px;font-weight:700;color:var(--navy);background:var(--mist);border:1px solid #D6DDE4;border-radius:50%;width:34px;height:34px;display:inline-grid;place-items:center;flex:0 0 auto;transform:translateY(-2px)}
.tc-sec p{color:#3A4652;font-size:17.5px}
.tc-sec ul{margin:0 0 1em;padding-left:22px;color:#3A4652;font-size:17.5px}
.tc-sec li{margin-bottom:10px;padding-left:4px}
.tc-sec li::marker{color:var(--gold-dark)}
.tc-sec strong{color:var(--navy)}
.tc-sec a{color:var(--navy);font-weight:600}
.tc-sec a:hover{color:var(--gold-dark)}
.tc-contact{list-style:none;padding:22px 24px!important;background:var(--paper);border:1px solid #E1E6EB;border-left:4px solid var(--gold);border-radius:6px}
.tc-contact li{padding-left:0;margin-bottom:8px}
.tc-contact li:last-child{margin-bottom:0}
.tc-note{margin-top:40px;padding:20px 24px;border-radius:6px;background:var(--mist);color:var(--muted);font-size:16px}
.tc-note a{color:var(--navy);font-weight:600}

@media (max-width:980px){
  .tc-wrap{grid-template-columns:1fr;gap:36px;padding-top:48px;padding-bottom:72px}
  .tc-toc{position:static;max-height:none}
  .tc-banner{padding-top:64px;padding-bottom:64px}
}
`;

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------- */
export default function Terms() {
  return (
    <>
      <style>{css}</style>

      <section className="tc-banner">
        <div className="container">
          <h1>Terms &amp; Conditions</h1>
          <p>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <div className="container tc-wrap">
        <nav className="tc-toc" aria-label="Terms and conditions contents">
          <h2>Contents</h2>
          <ol>
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
            ))}
          </ol>
        </nav>

        <article className="tc-content">
          <p className="tc-intro">
            Please read these terms carefully. They set out the basis on which {COMPANY} provides
            property letting, maintenance and cleaning services.
          </p>
          {sections.map((s) => (
            <section className="tc-sec" id={s.id} key={s.id}>
              <h2>{s.title}</h2>
              {s.body}
            </section>
          ))}
          <p className="tc-note">
            See also our <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </article>
      </div>
    </>
  );
}